/*
 * The browser platform. Two of the five capabilities exist here — notifications (once the person
 * has granted them) and the app badge — and the others degrade honestly rather than pretending:
 * a browser tab cannot raise its own window, and a deep link arrives as a normal navigation, so
 * there is nothing to subscribe to.
 *
 * Nothing here asks for a permission on its own: `notifyPermission()` reads the standing answer and
 * `requestNotifyPermission()` opens the dialog, and the only caller of the second one is the switch
 * on the notification settings screen, where the person can see what they are switching on.
 */

import type { NotifyPermission, Platform, PlatformCapabilities } from "./types.ts"

// The badging API is not in every engine, and the DOM types declare it as always present; the two
// calls are therefore made through an optional shape rather than on `navigator` directly.
interface BadgeNavigator {
	setAppBadge?: (count?: number) => Promise<void>
	clearAppBadge?: () => Promise<void>
}

const badge: BadgeNavigator = navigator

export const webPlatform: Platform = {
	capabilities(): PlatformCapabilities {
		return {
			notify: "Notification" in globalThis && Notification.permission === "granted",
			badge: typeof badge.setAppBadge === "function",
			focusWindow: false,
			deepLink: false,
		}
	},

	notifyPermission(): NotifyPermission {
		if (!("Notification" in globalThis)) return "unsupported"
		return Notification.permission
	},

	// A prompt the person closes without choosing rejects in some engines and answers "default" in
	// others; both mean the same thing — nobody has decided — so both end there.
	requestNotifyPermission(): Promise<NotifyPermission> {
		if (!("Notification" in globalThis)) return Promise.resolve("unsupported")
		return Notification.requestPermission().catch<NotifyPermission>(() => "default")
	},

	// Not `async`: nothing here waits, and the promise is only what the interface asks for.
	notify(n): Promise<void> {
		if (webPlatform.capabilities().notify) {
			const shown = new Notification(n.title, { body: n.body, tag: n.tag })
			if (n.onClick) shown.onclick = n.onClick
		}
		return Promise.resolve()
	},

	setBadge(count): void {
		if (count > 0) void badge.setAppBadge?.(count)
		else void badge.clearAppBadge?.()
	},

	focusWindow(): void {
		// A tab cannot raise itself; the notification click does it instead.
	},

	openExternal(url): void {
		// noopener keeps the opened page from reaching back into this one through window.opener.
		globalThis.open(url, "_blank", "noopener,noreferrer")
	},

	onDeepLink(): () => void {
		return () => {
			// Nothing subscribed: in a browser a deep link is an ordinary navigation.
		}
	},
}
